import React, { SyntheticEvent } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { Label } from '../NewDesign/Inputs/TextField';
import {
  useGetAddressByPlaceId,
  useGetAddressSuggestions,
} from '../../../Lib/Hooks/address.hooks';
import { Address, Suggestion } from './types';

type AutoCompleteAddressProps = {
  label: string;
  country: string;
  disabled: boolean;
  onChange: (data: Address) => void;
  value: string;
  onSelectSuggestion: (value: Suggestion | string | null) => void;
  fieldInputValue: string;
  onFieldInputValueChange: (value: string) => void;
};

function AutoCompleteAddress({
  label,
  country,
  disabled,
  onChange,
  value,
  onSelectSuggestion,
  fieldInputValue,
  onFieldInputValueChange,
}: AutoCompleteAddressProps) {
  const { isLoading, suggestions, setSuggestions } = useGetAddressSuggestions({
    value,
    fieldInputValue,
    country,
  });
  const getAddressByPlaceId = useGetAddressByPlaceId();

  const handleSelectSuggestion = (
    event: SyntheticEvent<Element, Event>,
    newValue: Suggestion | string | null,
  ) => {
    setSuggestions(newValue ? [newValue, ...suggestions] : suggestions);
    onSelectSuggestion(newValue);
    if (newValue && typeof newValue !== 'string' && newValue.place_id) {
      getAddressByPlaceId.mutate(newValue.place_id, {
        onSuccess: (data) => {
          onChange(data);
        },
      });
    }
  };

  return (
    <>
      <Label
        label={`${label}*`}
        subLabel="*Country needs to be selected first."
      />
      <Autocomplete
        disabled={!country || disabled}
        id={`address-completion-${label}`}
        getOptionLabel={(option: string | Suggestion) =>
          typeof option === 'string' ? option : option.description
        }
        loading={isLoading}
        filterOptions={(x) => x}
        options={suggestions}
        autoComplete
        includeInputInList
        filterSelectedOptions
        noOptionsText="No locations"
        isOptionEqualToValue={(
          option: string | Suggestion,
          v: string | Suggestion,
        ) => {
          if (
            typeof option !== 'string' &&
            option.description &&
            typeof v !== 'string' &&
            v.description
          ) {
            return option.description === v.description;
          }
          return false;
        }}
        value={value}
        onChange={handleSelectSuggestion}
        onInputChange={(event, newInputValue) => {
          onFieldInputValueChange(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            variant="standard"
          />
        )}
      />
    </>
  );
}

export default AutoCompleteAddress;
